import type { BureauCredential } from '../../types';

export const BUREAUS = ['equifax', 'experian', 'transunion', 'chexsystems'] as const;
export const CORE_THREE_BUREAUS = ['equifax', 'experian', 'transunion'] as const;

export const BUREAU_LABELS: Record<string, string> = {
  chexsystems: 'ChexSystems',
  equifax: 'Equifax',
  experian: 'Experian',
  transunion: 'TransUnion',
};

export type NewBureauRequestFormKind =
  | 'lock_request'
  | 'unlock_request'
  | 'permanent_unblock'
  | 'extension'
  | 'special_case';

export type BureauToolbarAction = NewBureauRequestFormKind | 'first_time_block' | 'block_bureaus';

export const normalizeOwnerLabel = (value: string) =>
  String(value ?? '').replace(/\s*\(you\)\s*$/i, '').trim().toLowerCase();

export const stripGraphSuffix = (value: string) =>
  String(value ?? '').replace(/__stg3_c$|__stg_c$|__dev_c$|__stg$|__c$/i, '');

export type BureauRelatedRow = {
  id: string;
  creditBureauId: string;
  bureauName: string;
  stage: string;
  authority: string;
};

export function formatBureauStageDisplay(c: BureauCredential | undefined): string {
  if (!c) return '—';
  const s = String(c.status ?? '').trim().toLowerCase();
  if (!s || s === 'unknown') return '—';
  if (s === 'unlocked') return 'Unblocked';
  if (s === 'locked') return 'Locked';
  if (s === 'monitored') return 'Monitored';
  if (s === 'permanently_unblocked') return 'Permanently Unblocked';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function normalizedBureauStatus(c: BureauCredential | undefined): string {
  return String(c?.status ?? '').trim().toLowerCase();
}

export function isUnblockedBureau(c: BureauCredential | undefined): boolean {
  const s = normalizedBureauStatus(c);
  return s === 'unlocked' || s === 'unblocked';
}

export function isLockedOrBlockedBureau(c: BureauCredential | undefined): boolean {
  const s = normalizedBureauStatus(c);
  return s === 'locked' || s === 'blocked';
}

export function buildBureauRelatedRows(bc: Record<string, BureauCredential>): BureauRelatedRow[] {
  return BUREAUS.map((b) => {
    const c = bc[b];
    const id = c?.creditBureauId ? String(c.creditBureauId) : `bureau-${b}`;
    return {
      id,
      creditBureauId: c?.creditBureauId || '—',
      bureauName: BUREAU_LABELS[b] || b,
      stage: formatBureauStageDisplay(c),
      authority: c?.authority ? (c.authority === 'primary' ? 'Primary' : 'Secondary') : '—',
    };
  });
}
